import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "cit-analytics-session";
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 min

function ensureSessionId() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const { id, ts } = JSON.parse(raw);
      if (id && ts && Date.now() - ts < SESSION_TTL_MS) {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: Date.now() }));
        return id;
      }
    }
  } catch (e) {
    console.debug("cit-analytics: session storage read failed, regenerating", e);
    // corrupted storage — regenerate below
  }
  const id =
    (crypto?.randomUUID && crypto.randomUUID()) ||
    Math.random().toString(36).slice(2) + Date.now().toString(36);
  sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: Date.now() }));
  return id;
}

function send(payload) {
  const url = `${API}/api/track`;
  const body = JSON.stringify({
    session_id: ensureSessionId(),
    referrer: document.referrer || "",
    screen: `${window.innerWidth}x${window.innerHeight}`,
    ...payload,
  });
  // Prefer sendBeacon to survive navigation; fall back to fetch.
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon(url, blob)) return;
  }
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}

/**
 * Fires a "pageview" event on every route change. Also skips the admin panel
 * so we don't pollute stats with our own visits.
 */
export function useTracking() {
  const location = useLocation();
  const lastPath = useRef(null);

  useEffect(() => {
    const path = location.pathname + (location.search || "");
    if (path === lastPath.current) return;
    lastPath.current = path;
    if (location.pathname.startsWith("/admin")) return;
    send({ type: "pageview", path });
  }, [location.pathname, location.search]);
}
